<template>
<v-dialog :value="value" width="600" persistent>
  <v-card color="fill">
    <v-card-title :class="title">
      <span>Copy / Move Ad Feature</span>
      <v-col cols="auto" class="ml-auto my-auto py-0 pr-0 d-inline-flex">
        <p class="pa-0 my-auto mr-2 text-overline">
          Action:
        </p>
        <v-btn-toggle
          v-model="action"
          dense
          color="primary"
          @change="getAds"
          mandatory>
          <v-btn value="copy">Copy</v-btn>
          <v-btn value="move">Move</v-btn>
        </v-btn-toggle>
      </v-col>
    </v-card-title>
    <v-container class="ma-0 pa-0 pt-6 px-6" fluid>
      <v-form ref="form" v-model="valid">
        <v-row dense>
          <v-col>
            <v-autocomplete
              v-model="destinationAd"
              label="Destination Ad"
              :items="ads"
              :class="body"
              :disabled="loading || noValidAds"
              :loading="loadingAds"
              item-text="ad_name"
              item-value="id"
              background-color="input"
              :menu-props="{ maxHeight: 220, rounded: '' }"
              outlined dense
              return-object
              @change="getFeaturePositions">
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item dense v-on="on" v-bind="attrs">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.ad_name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.ad_group_name }} 
                  </v-list-item-subtitle>
                </v-list-item-content>
                </v-list-item> 
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-row justify="center" align="center" dense>
          <v-col>
            <v-autocomplete 
              v-model="destinationPage"
              label="Destination Page"
              :items="pages"
              :class="body"
              :disabled="loading || noValidAds"
              item-text="name"
              item-value="id"
              background-color="input"
              autocomplete="off"
              outlined dense
              return-object
              @change="getFeaturePositions"
              :menu-props="{ maxHeight: 220, rounded: '' }">
            </v-autocomplete>
          </v-col>
          <v-col>
            <v-autocomplete 
              v-model="destinationFeaturePosition"
              label="Destination Position"
              :items="featurePositions"
              :class="body"
              :disabled="loading || noValidAds"
              :loading="loadingFeaturePositions"
              item-text="name"
              item-value="id"
              background-color="input"
              autocomplete="off"
              outlined dense
              return-object
              :menu-props="{ maxHeight: 220, rounded: '' }">
            </v-autocomplete>
          </v-col>
        </v-row>
        <v-divider />
      </v-form>
    </v-container>
    <v-toolbar flat color="fill">
      <v-col cols="auto">
        <span v-if="noValidAds" class="warning--text">
          <v-icon color="warning">mdi-alert-circle-outline</v-icon>
          No valid feature destinations found
        </span>
      </v-col>
      <v-spacer />
      <v-btn text :class="body" @click="$emit('update:value', false)">
        Cancel
      </v-btn>
      <v-btn 
        :disabled="moveCopyDisabled" 
        :color="action === 'move' ? 'warning' : 'save'" 
        :class="body" 
        class="white--text ml-2" 
        @click.stop="openConfirmDialog">
        {{ selectedAction.text }}
      </v-btn>
    </v-toolbar>
  </v-card>
  <FeatureMoveDialog 
    v-if="confirmDialog" 
    v-model="confirmDialog" 
    :move="action === 'move'"
    :destinationAd="destinationAd"
    @cancel="closeConfirmDialog" 
    @confirmAction="confirmAction">
  </FeatureMoveDialog>
  <FeaturePromoWarnings
    v-if="warningsModal" 
    v-model="warningsModal" 
    :selectedAd="selectedAd" 
    :gappedPromos="gappedPromos"
    :move="action === 'move'"
    @cancel="closeWarningsModal" 
    @confirmAction="confirmAction">
  </FeaturePromoWarnings>
</v-dialog>
</template>
<script>
import { utils } from '@/mixins/utils'
import { displayHelpers } from '@/mixins/display-helpers'
import { notification } from '@/mixins/notification'
import { format } from '@/mixins/format'
import { displayAlert } from '@/mixins/alert'
import FeatureMoveDialog from '@/components/features/feature-move-dialog.vue'
import Ads from '@/axios/ad-endpoints'
import Features from '@/axios/feature-endpoints'
import FeaturePromoWarnings from '@/components/features/promotions/FeaturePromoWarnings.vue'
export default {
  name: 'FeatureMoveCopy',
  mixins: [displayHelpers, format, notification, displayAlert, utils],
  components: { FeatureMoveDialog, FeaturePromoWarnings },
  data() {
    return {
      confirmDialog: false,
      destinationAd: {},
      destinationFeaturePosition: {},
      destinationPage: {},
      ads: [],
      warningsModal: false,
      gappedPromos: [],
      loadingAds: false,
      featurePositions: [],
      loadingFeaturePositions: false,
      copyInProgress: false,
      valid: true,
      noValidAds: false,
      action: 'copy',
      actions: [{ text: 'Copy', value: 'copy' }, { text: 'Move', value: 'move'}]
    }
  },
  props: {
    value: Boolean,
    selectedAd: Object, 
    selectedFeature: Object,
    pages: Array,
    adParty: Object
  },
  async created() {
    await this.getAds()
  },
  computed: {
    selectedAction() {
      return this.actions.find(a => a.value === this.action)
    },
    moveCopyDisabled () {
      return this.loading || (!this.destinationAd?.id || !this.destinationPage?.id || !this.destinationFeaturePosition?.id)
    },
    loading () {
      return this.loadingAds || this.loadingFeaturePositions || this.copyInProgress
    }
  },
  methods: {
    async getAds () {
      this.loadingAds = true
      try {
        const params = { 
          ad_id: this.selectedAd.id, 
          feature_id: this.selectedFeature.id, 
          item_source: this.adParty.distribution_center,
          non_promo_only: (this.selectedFeature.promo_item_group_ids.length === 0)
        }
        const { data } = (this.action === 'move')
          ? await Ads.getValidMoveDestinations(params)
          : await Ads.getValidCopyDestinations(params)
        this.ads = data
        if (this.ads.length > 0) {
          if (this.noValidAds) this.noValidAds = false
          this.destinationAd = this.ads[0]
          this.destinationPage = this.pages[0]
          await this.getFeaturePositions()
        } else {
          this.noValidAds = true
        }
      } catch (e) {
        console.error(e)
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Failed to retrieve ad list: ${e}` })
      } finally {
        this.loadingAds = false
      }
    },
    async getFeaturePositions () {
      if (!this.destinationAd?.id || !this.destinationPage?.id) return
      this.loadingFeaturePositions = true
      this.featurePositions = []
      try {
        const { data } = await Features.getEmptyPagePositions(this.destinationAd.id, this.destinationPage.id)
        this.featurePositions = data
        if (data?.length > 0) {
          this.destinationFeaturePosition = this.featurePositions[0]
        }
      } catch (e) {
        console.error(e)
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Failed to retrieve feature positions: ${e}` })
      } finally {
        this.loadingFeaturePositions = false
      }
    },
    async copy () {
      if (!this.destinationAd?.id || !this.destinationFeaturePosition?.id) return
      const movingFeature = (this.action === 'move')
      const payload = {
        move: movingFeature,
        item_source: this.destinationAd.item_source,
        ad_id: this.selectedAd.id,
        ad_group_id: this.adParty.id,
        start_date: this.destinationAd.start_date,
        end_date: this.destinationAd.end_date,
        feature_id: this.selectedFeature.id,
        dest_feature_id: this.destinationFeaturePosition.feature_id,
        dest_ad_id: this.destinationAd.id,
        dest_pig_ids: this.destinationAd.dest_pig_ids || [],
        dest_promo_ids: this.destinationAd.dest_promo_ids || []
      }
      this.copyInProgress = true
      try {
        if (movingFeature) {
          await Features.moveAdFeature(payload)
        } else {
          await Features.copyAdFeature(payload)
        }
        const successText = movingFeature ? 'Feature Moved' : `Feature Copied`
        this.$store.dispatch('setSnackbar', { status: 'success', text: successText })
        this.$emit('update:value', false)
      } catch (e) {
        console.error(e)
        this.$store.dispatch('setSnackbar', { status: 'error', text: `Failed to ${this.action} feature: ${e}` })
      } finally {
        this.copyInProgress = false
      }
    },
    confirmAction () {
      this.closeConfirmDialog()
      this.closeWarningsModal()
      this.copy()
    },
    async openConfirmDialog () {
      await this.checkPromoGap()
      if (this.gappedPromos.length > 0) {
        return this.warningsModal = true
      }
      this.confirmDialog = true
    },
    closeConfirmDialog () {
      this.confirmDialog = false
    },
    closeWarningsModal () {
      this.warningsModal = false
    },
    async checkPromoGap () {
      if (!this.destinationAd?.id) return
      this.gappedPromos = []
      try {
        const params = {
          source_ad_id: this.selectedAd.id,
          target_ad_id: this.destinationAd.id, 
          source_feature_id: this.selectedFeature.id, 
          item_source: this.adParty.distribution_center
        }
        const { data } = await Features.checkPromoGap(params)
        this.gappedPromos = data
      } catch (err) {
        this.handleError(err)
      }
    }
  }
}
</script>
